"use client";

/**
 * DesktopMock — animated mock of the askFinz desktop app surface for
 * /desktop.
 *
 * The desktop client is the offline-friendly cousin of the cloud product:
 * a curated set of local-first models, no agent fleets, no frontier
 * vision/long-context. The mock is intentionally grounded — it should read
 * as "this is the version that works on a plane", not as a frontier-spec
 * demo.
 *
 * Visual:
 *   - Native-style macOS window (3 traffic-light dots, single title strip).
 *   - Sidebar of workspaces available offline (Chat, Code, Notes, Local
 *     files) with the active item highlighted.
 *   - Main pane: a chat view with a "Local model · downloaded · 4.7GB"
 *     indicator and a connection badge "Offline · using local model" with a
 *     neutral/yellow dot.
 *   - Right rail: greyed-out "Cloud features (coming soon)" chips for
 *     vision / long-context / agent fleets, each with a "Cloud only" pill.
 *   - Bottom caption: "Use Cloud at *.askfinz.ai for the full agent fleet".
 *
 * Loop:
 *   Every ~9s the local model finishes a response, the user types a follow-
 *   up, and the local model responds again. Loop runs forever. No frontier
 *   fanfare.
 *
 * Pure presentation. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  HardDrive,
  ChevronDown,
  MessageSquare,
  Code2,
  StickyNote,
  Folder,
  Send,
  Eye,
  BookOpen,
  Bot,
  Cloud,
  WifiOff,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";

type ModelOpt = {
  id: "local-fast" | "local-balanced" | "local-coder";
  label: string;
  size: string;
};

const LOCAL_MODELS: ModelOpt[] = [
  { id: "local-fast",     label: "Local · fast",      size: "4.7GB" },
  { id: "local-balanced", label: "Local · balanced",  size: "7.2GB" },
  { id: "local-coder",    label: "Local · coder",     size: "5.1GB" },
];

type Workspace = {
  id: string;
  label: string;
  icon: typeof MessageSquare;
  active?: boolean;
};

const WORKSPACES: Workspace[] = [
  { id: "chat",  label: "Chat",        icon: MessageSquare, active: true },
  { id: "code",  label: "Code",        icon: Code2 },
  { id: "notes", label: "Notes",       icon: StickyNote },
  { id: "files", label: "Local files", icon: Folder },
];

type CloudOnlyFeature = {
  label: string;
  icon: typeof Eye;
  detail: string;
};

const CLOUD_FEATURES: CloudOnlyFeature[] = [
  { label: "Vision",         icon: Eye,     detail: "Image + screen input" },
  { label: "Long context",   icon: BookOpen, detail: "200k+ token windows" },
  { label: "Agent fleets",   icon: Bot,     detail: "Multi-agent runs" },
];

type Turn =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string };

const CONVERSATIONS: Turn[][] = [
  [
    { kind: "user", text: "Summarise this contract folder in three bullets." },
    {
      kind: "assistant",
      text:
        "Three contracts, two NDAs and one MSA. All three expire on or before Aug 2026. The MSA has an unusual unilateral termination clause at §9 — worth flagging before renewal.",
    },
    { kind: "user", text: "Draft a counter on §9." },
    {
      kind: "assistant",
      text:
        "Draft: replace unilateral termination with mutual termination on 60-day written notice, plus a 30-day cure period for material breach. I've saved it as `counter-msa-§9.md` in your Notes workspace.",
    },
  ],
];

// First-paint snapshot: the chat thread already shows the first user message
// + first local-model reply, so the pane never reads as empty. The script
// continues with the user follow-up + the second local response.
const INITIAL_CONV: Turn[] = CONVERSATIONS[0].slice(0, 2);
const INITIAL_TURN_COUNT = INITIAL_CONV.length;

export function DesktopMock() {
  const [conv, setConv] = useState<Turn[]>(INITIAL_CONV);
  const [phase, setPhase] = useState<"idle" | "thinking" | "typing-user">("idle");
  const [draftUser, setDraftUser] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runLoop = () => {
      const script = CONVERSATIONS[0];
      // Loop reset = partial-fill (first 2 turns already shown).
      setConv(INITIAL_CONV);
      setDraftUser("");
      setPhase("idle");

      let cursor = 600;
      // Skip the first 2 turns — they're already on screen from the start.
      script.slice(INITIAL_TURN_COUNT).forEach((turn, i) => {
        if (turn.kind === "user") {
          // Show "typing-user" then commit the message.
          schedule(() => {
            setPhase("typing-user");
            // Type the text in chunks.
            const chunks = Math.ceil(turn.text.length / 5);
            for (let c = 1; c <= chunks; c++) {
              schedule(() => setDraftUser(turn.text.slice(0, c * 5)), c * 50);
            }
            schedule(() => {
              setConv((cur) => [...cur, turn]);
              setDraftUser("");
              setPhase("idle");
            }, chunks * 50 + 250);
          }, cursor);
          cursor += turn.text.length * 12 + 800;
        } else {
          // Assistant: thinking pause, then commit the response.
          schedule(() => setPhase("thinking"), cursor);
          schedule(() => {
            setConv((cur) => [...cur, turn]);
            setPhase("idle");
          }, cursor + 1300);
          cursor += 1300 + Math.min(2400, turn.text.length * 10) + 600;
          void i;
        }
      });

      // Restart loop.
      schedule(runLoop, cursor + 1800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runLoop, 400);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[14px] border hairline-strong bg-card/85 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* macOS-style title bar */}
        <div className="relative flex items-center px-3 h-9 border-b hairline bg-foreground/[0.04]">
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-foreground/20" />
            <span className="h-3 w-3 rounded-full bg-foreground/20" />
            <span className="h-3 w-3 rounded-full bg-foreground/20" />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 text-[11px] tracking-[0.04em] text-foreground/65 font-medium hidden md:block">
            askFinz Desktop — Chat
          </div>
          <div className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[#d4a017]/80" />
            <WifiOff className="h-3 w-3" />
            Offline · using local model
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[520px]">
          {/* Sidebar */}
          <aside className="col-span-3 md:col-span-2 border-r hairline bg-foreground/[0.03] p-2 text-[12px]">
            <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              Workspaces
            </div>
            <div className="space-y-0.5">
              {WORKSPACES.map((w) => {
                const Icon = w.icon;
                return (
                  <div
                    key={w.id}
                    className={
                      "flex items-center gap-2 px-2 py-1.5 rounded transition " +
                      (w.active
                        ? "bg-foreground text-background"
                        : "text-foreground/70 hover:bg-foreground/5")
                    }
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    <span className="text-[11px] truncate">{w.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 px-2 pb-1 mb-1 border-t hairline pt-3 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              Recent
            </div>
            <div className="space-y-0.5 text-[11px] text-foreground/65">
              <div className="px-2 py-1 truncate">Contract review</div>
              <div className="px-2 py-1 truncate">Travel notes · Lisbon</div>
              <div className="px-2 py-1 truncate">README · refactor</div>
            </div>
          </aside>

          {/* Main chat pane */}
          <main className="col-span-9 md:col-span-7 flex flex-col bg-background">
            {/* Top status row — local model indicator + curated picker */}
            <div className="flex items-center gap-2 px-4 py-2 border-b hairline">
              <div className="inline-flex items-center gap-1.5 rounded-md border hairline bg-foreground/[0.04] px-2 py-1 text-[10.5px] font-mono text-foreground/75">
                <HardDrive className="h-3 w-3" />
                Local model · downloaded · {LOCAL_MODELS[0].size}
              </div>
              <button className="inline-flex items-center gap-1 rounded-md border hairline bg-background px-2 py-1 text-[10.5px] text-foreground/75 hover:bg-foreground/5 transition">
                <span>{LOCAL_MODELS[0].label}</span>
                <ChevronDown className="h-3 w-3 text-foreground/65" />
              </button>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                <CheckCircle2 className="h-3 w-3" />
                Verified · sha256
              </span>
            </div>

            {/* Conversation */}
            <div className="flex-1 px-4 py-4 space-y-3 overflow-hidden">
              {conv.map((turn, i) => (
                <Bubble key={i} turn={turn} />
              ))}
              {phase === "thinking" && (
                <div className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase text-foreground/65 font-mono animate-[blur-fade-in_0.3s_ease-out]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-foreground/55 animate-[typing-dot_1.4s_ease-in-out_infinite]" />
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-foreground/55 animate-[typing-dot_1.4s_ease-in-out_infinite_0.2s]" />
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-foreground/55 animate-[typing-dot_1.4s_ease-in-out_infinite_0.4s]" />
                  <span className="ml-1">Local model thinking…</span>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t hairline px-3 py-2 bg-background">
              <div className="flex items-end gap-2">
                <div className="flex-1 rounded-md border hairline bg-foreground/[0.02] px-3 py-2 text-[12px] text-foreground/85 min-h-[36px]">
                  {phase === "typing-user" ? (
                    <>
                      <span>{draftUser}</span>
                      <span className="inline-block h-3 w-1 bg-foreground/60 align-middle ml-0.5 animate-pulse" />
                    </>
                  ) : (
                    <span className="text-foreground/65">
                      Ask the local model anything · runs on this machine
                    </span>
                  )}
                </div>
                <button className="p-1.5 rounded-md bg-foreground text-background" aria-hidden tabIndex={-1}>
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </main>

          {/* Right rail — cloud-only features */}
          <aside className="hidden md:flex md:col-span-3 flex-col border-l hairline bg-foreground/[0.02] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2 inline-flex items-center gap-1.5">
              <Cloud className="h-3 w-3" />
              Cloud features (coming soon)
            </div>
            <div className="space-y-2">
              {CLOUD_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    className="rounded-md border hairline bg-background/60 p-2.5 opacity-65"
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-foreground/65" />
                      <span className="text-[11.5px] font-medium text-foreground/75">{f.label}</span>
                      <span className="ml-auto px-1.5 py-0.5 rounded border hairline text-[9px] tracking-[0.14em] uppercase font-mono text-foreground/65">
                        Cloud only
                      </span>
                    </div>
                    <div className="mt-1 text-[10.5px] text-foreground/65 leading-snug">
                      {f.detail}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto pt-3 border-t hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono leading-relaxed">
              <span className="inline-flex items-center gap-1.5">
                <ArrowUpRight className="h-3 w-3" />
                Use Cloud at <span className="text-foreground/75 normal-case tracking-normal font-mono">*.askfinz.ai</span>
              </span>
              <div className="mt-1 normal-case tracking-normal text-foreground/65 text-[10.5px] font-sans">
                for the full agent fleet.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Bubble({ turn }: { turn: Turn }) {
  if (turn.kind === "user") {
    return (
      <div className="self-end ml-auto max-w-[80%] rounded-2xl rounded-br-md bg-foreground text-background px-3.5 py-2 text-[12.5px] leading-relaxed animate-[blur-fade-in_0.4s_ease-out]">
        {turn.text}
      </div>
    );
  }
  return (
    <div className="max-w-[85%] animate-[blur-fade-in_0.5s_ease-out]">
      <div className="inline-flex items-center gap-1.5 mb-1 text-[10px] tracking-[0.14em] uppercase text-foreground/65 font-mono">
        <HardDrive className="h-2.5 w-2.5" />
        Local · fast
      </div>
      <div className="rounded-2xl rounded-tl-md border hairline bg-foreground/[0.025] px-3.5 py-2 text-[12.5px] leading-relaxed text-foreground/85">
        {turn.text}
      </div>
    </div>
  );
}
